import { useState, type FormEvent } from "react";
import { Button, ErrorText, TextField } from "../../ui";
import { useEmulatorSignIn, useSignIn } from "./session";

function message(error: unknown): string {
  const code = typeof error === "object" && error !== null && "code" in error ? String(error.code) : null;
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return "Sign-in was cancelled.";
  if (code === "auth/popup-blocked") return "Your browser blocked the sign-in window. Allow pop-ups and try again.";
  return "Sign-in failed. Please try again.";
}

export function SignInPage() {
  const signIn = useSignIn();
  const emulatorSignIn = useEmulatorSignIn();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(message(e));
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-bg px-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-text">KitWork+</h1>
        <p className="mt-1 text-sm text-muted">One inbox, your workspaces, your team.</p>
        <Button variant="primary" className="mt-6 w-full" onClick={() => void run(signIn)} disabled={busy}>
          {busy ? "Signing in…" : "Sign in with Google"}
        </Button>
        <div className="mt-3 min-h-5">{error === null ? null : <ErrorText>{error}</ErrorText>}</div>
        {emulatorSignIn === null ? null : (
          <EmulatorSignIn busy={busy} onSubmit={(email, name) => void run(() => emulatorSignIn(email, name))} />
        )}
      </div>
    </main>
  );
}

/** Local and test only: signs in to the Auth emulator as any Google user, without a popup. */
function EmulatorSignIn({ busy, onSubmit }: { busy: boolean; onSubmit: (email: string, name: string) => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit(email, name);
  }

  return (
    <form
      onSubmit={submit}
      aria-label="Emulator sign-in"
      className="mt-4 flex flex-col gap-3 border-t border-dashed border-border pt-4"
    >
      <p className="text-xs text-muted">Emulator only: sign in as any Google account.</p>
      <TextField label="Email" type="email" name="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      <TextField label="Display name" name="name" value={name} onChange={(e) => setName(e.target.value)} />
      <Button type="submit" disabled={busy}>
        Sign in to the emulator
      </Button>
    </form>
  );
}
